import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useSearchParams } from "react-router-dom";
import { Loader2, PackageSearch, Printer, Trash2 } from "lucide-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { Button } from "@/components/ui/Button";
import { Card, CardBody } from "@/components/ui/Card";
import { Input, Label } from "@/components/ui/Input";
import { SortableTableHeader, type SortDirection } from "@/components/ui/SortableTableHeader";
import { PageTitleWithHelp } from "@/components/ui/title-help";
import { useFloorPageScanCapture } from "@/lib/floorPageScanCapture";
import { playPairSuccessSound } from "@/lib/floorStationPairAudio";
import { FROM_QUERY_PARAM } from "@/lib/navigationTrail";
import { usePageBreadcrumb } from "@/lib/navigationTrailContext";
import {
  inboundReceiveRmaCountCheckedMessage,
  inboundReceiveRmaFinishWithDiscrepancyMessage,
} from "@integrator-src/api/shipping-inbound/shipping-inbound-receive-rma-devices";
import { InboundZplPrinterHeaderButton } from "./InboundZplPrinterHeaderButton";
import {
  formatInboundArrivalUnitHeading,
  parseInboundArrivalUnitKind,
} from "./inboundArrivalUnitKind";
import { applyInboundReceiveArrivalRoute, deleteInboundReceiveRmaDeviceCheckIn, fetchInboundReceiveRmaDeviceCheckIns, matchInboundReceiveRmaDevices, saveInboundReceiveRmaDeviceCheckIn, type InboundReceiveRmaDeviceCheckIn } from "./inboundApi";
import { reprintInboundReceiveArrivalPackageLabel } from "./inboundReceiveArrivalLabelPrint";
import { parseInboundReceiveArrivalPackageSearch } from "./inboundReceiveNavigation";
import { inboundReceiveRouteLabel, type InboundReceiveRoute } from "./inboundReceiveRoute";
import { useInboundZplPrinter } from "./inboundZplPrinter";

type RmaCheckInSortKey = "added" | "device" | "sku" | "endUser";

function compareCheckInText(left: string, right: string): number {
  return left.localeCompare(right, undefined, { numeric: true, sensitivity: "base" });
}

export function InboundSpecialReceiveDeskPage(props: { route: InboundReceiveRoute }): JSX.Element {
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const crate = parseInboundReceiveArrivalPackageSearch(searchParams);
  const stateCrate = location.state as {
    arrivalPackageNo?: number;
    arrivalPackageId?: string;
    arrivalId?: string;
    arrivalUnitKind?: string;
    rmaNumber?: string | null;
    rmaCountChecked?: boolean;
  } | null;
  const packageNo = crate?.arrivalPackageNo ?? stateCrate?.arrivalPackageNo ?? null;
  const packageId = crate?.arrivalPackageId ?? stateCrate?.arrivalPackageId ?? null;
  const arrivalId = crate?.arrivalId ?? stateCrate?.arrivalId ?? null;
  const unitKind = parseInboundArrivalUnitKind(
    crate?.arrivalUnitKind ?? stateCrate?.arrivalUnitKind ?? null,
  );
  const rmaNumber = (crate?.rmaNumber ?? stateCrate?.rmaNumber ?? "").trim();
  const rmaCountChecked = crate?.rmaCountChecked === true || stateCrate?.rmaCountChecked === true;
  const rmaReceiveQuantityDiscrepancy =
    crate?.rmaReceiveQuantityDiscrepancy === true || searchParams.get("rma_count") === "discrepancy";
  const label = inboundReceiveRouteLabel(props.route);
  const { station, setStation, selectedPrinter, unavailableMessage, documents } = useInboundZplPrinter();
  const [printError, setPrintError] = useState<string | null>(null);
  const [deviceDraft, setDeviceDraft] = useState("");
  const [countChecked, setCountChecked] = useState(rmaCountChecked);
  const [checkInFinished, setCheckInFinished] = useState(false);
  const [pendingSerial, setPendingSerial] = useState<string | null>(null);
  const [sortKey, setSortKey] = useState<RmaCheckInSortKey>("added");
  const [sortDirection, setSortDirection] = useState<SortDirection>("desc");
  const deviceScanRef = useRef<HTMLInputElement>(null);
  const autoPrintAttempt = useRef(false);
  const queryClient = useQueryClient();
  const showDeviceScan = props.route === "rma" && !countChecked;
  const checkInsQuery = useQuery({
    queryKey: ["shipping", "inbound", "rma-device-check-ins", rmaNumber],
    queryFn: () => fetchInboundReceiveRmaDeviceCheckIns(rmaNumber),
    enabled: props.route === "rma" && rmaNumber !== "",
  });
  const savedDevices = checkInsQuery.data?.devices ?? [];
  const savedSerials = savedDevices.map((device) => device.deviceSerial);
  const scannedSerials =
    pendingSerial != null && !savedSerials.some((serial) => serial.toUpperCase() === pendingSerial.toUpperCase())
      ? [...savedSerials, pendingSerial]
      : savedSerials;
  const checkInRows = useMemo(() => {
    const rows: InboundReceiveRmaDeviceCheckIn[] = [...savedDevices];
    if (
      pendingSerial != null &&
      !rows.some((device) => device.deviceSerial.toUpperCase() === pendingSerial.toUpperCase())
    ) {
      rows.unshift({
        id: "pending",
        deviceSerial: pendingSerial,
        sku: null,
        endUserName: savedDevices[0]?.endUserName ?? null,
        checkedInAt: new Date().toISOString(),
      });
    }
    const direction = sortDirection === "asc" ? 1 : -1;
    rows.sort((left, right) => {
      if (sortKey === "device") return compareCheckInText(left.deviceSerial, right.deviceSerial) * direction;
      if (sortKey === "sku") return compareCheckInText(left.sku ?? "", right.sku ?? "") * direction;
      if (sortKey === "endUser") return compareCheckInText(left.endUserName ?? "", right.endUserName ?? "") * direction;
      return compareCheckInText(left.checkedInAt, right.checkedInAt) * direction || compareCheckInText(left.id, right.id) * direction;
    });
    return rows;
  }, [pendingSerial, savedDevices, sortDirection, sortKey]);

  function toggleCheckInSort(key: RmaCheckInSortKey) {
    if (key === sortKey) {
      setSortDirection((current) => (current === "asc" ? "desc" : "asc"));
      return;
    }
    setSortKey(key);
    setSortDirection(key === "added" ? "desc" : "asc");
  }

  const checkInMutation = useMutation({
    mutationFn: (serial: string) =>
      saveInboundReceiveRmaDeviceCheckIn({
        rmaNumber,
        deviceSerial: serial,
        packageId,
      }),
    onMutate: (serial) => {
      setPendingSerial(serial);
      setCheckInFinished(false);
      setDeviceDraft("");
    },
    onSuccess: (result) => {
      queryClient.setQueryData(["shipping", "inbound", "rma-device-check-ins", rmaNumber], result);
      setPendingSerial(null);
      setPrintError(null);
    },
    onError: (error) => {
      setPendingSerial(null);
      setPrintError(error instanceof Error ? error.message : String(error));
    },
  });

  const deleteCheckInMutation = useMutation({
    mutationFn: (checkInId: string) => deleteInboundReceiveRmaDeviceCheckIn({ rmaNumber, checkInId }),
    onSuccess: (result) => {
      queryClient.setQueryData(["shipping", "inbound", "rma-device-check-ins", rmaNumber], result);
      setCheckInFinished(false);
      setPrintError(null);
    },
    onError: (error) => setPrintError(error instanceof Error ? error.message : String(error)),
  });

  const deviceMatchQuery = useQuery({
    queryKey: ["shipping", "inbound", "rma-device-match", "desk", rmaNumber, scannedSerials],
    queryFn: () => matchInboundReceiveRmaDevices(scannedSerials, rmaNumber),
    enabled: showDeviceScan && checkInFinished && scannedSerials.length > 0 && rmaNumber !== "",
  });
  const finish = checkInFinished ? deviceMatchQuery.data?.finish ?? null : null;
  const readyToPrint =
    checkInFinished &&
    finish != null &&
    finish.canFinish &&
    (rmaNumber === "" || finish.rmaNumber === rmaNumber);
  const quantityDiscrepancy = readyToPrint && finish != null && finish.quantityDiscrepancy;
  const pageTitle =
    props.route === "rma" ? (rmaNumber !== "" ? rmaNumber : finish?.rmaNumber ?? label) : label;

  usePageBreadcrumb(pageTitle);

  useEffect(() => {
    if (props.route !== "rma") return;
    if ((searchParams.get(FROM_QUERY_PARAM) ?? "").trim() !== "") return;
    const next = new URLSearchParams(searchParams);
    next.set(FROM_QUERY_PARAM, "/shipping/receive");
    setSearchParams(next, { replace: true, state: location.state });
  }, [location.state, props.route, searchParams, setSearchParams]);

  function addDeviceSerial(raw: string) {
    const serial = raw.trim();
    if (serial === "" || rmaNumber === "") return;
    if (scannedSerials.some((row) => row.toUpperCase() === serial.toUpperCase())) {
      setDeviceDraft("");
      return;
    }
    checkInMutation.mutate(serial);
  }

  useFloorPageScanCapture({
    enabled: showDeviceScan,
    inputRef: deviceScanRef,
    onScan: addDeviceSerial,
  });

  useEffect(() => {
    if (!showDeviceScan) return;
    deviceScanRef.current?.focus();
  }, [showDeviceScan]);

  const printStickerMutation = useMutation({
    mutationFn: async () => {
      if (!readyToPrint || finish == null || packageId == null) {
        throw new Error("Scan at least one device before printing the sticker.");
      }
      const rmaId = Number.parseInt(finish.rmaId, 10);
      if (!Number.isSafeInteger(rmaId)) {
        throw new Error("Scan at least one device before printing the sticker.");
      }
      await applyInboundReceiveArrivalRoute({
        scan: "",
        packageId,
        receive_route: "rma",
        rmaId,
        rmaNumber: finish.rmaNumber,
        deviceSerials: scannedSerials,
      });
      if (arrivalId == null || selectedPrinter.trim() === "") {
        throw new Error("Pick a label printer to print the sticker.");
      }
      await reprintInboundReceiveArrivalPackageLabel({
        arrivalId,
        packageId,
        printer: selectedPrinter,
      });
      return finish.rmaNumber;
    },
    onSuccess: (printedRmaNumber) => {
      playPairSuccessSound();
      setPrintError(null);
      setCountChecked(true);
      const next = new URLSearchParams(searchParams);
      next.set("rma_number", printedRmaNumber);
      next.set("rma_count", quantityDiscrepancy ? "discrepancy" : "ok");
      setSearchParams(next, { replace: true, state: location.state });
    },
    onError: (error) => setPrintError(error instanceof Error ? error.message : String(error)),
  });

  useEffect(() => {
    if (!checkInFinished || !readyToPrint || countChecked || printStickerMutation.isPending) return;
    if (selectedPrinter.trim() === "") return;
    if (autoPrintAttempt.current) return;
    autoPrintAttempt.current = true;
    printStickerMutation.mutate();
  }, [
    checkInFinished,
    readyToPrint,
    countChecked,
    selectedPrinter,
    printStickerMutation.isPending,
    printStickerMutation,
  ]);

  const reprintMutation = useMutation({
    mutationFn: () => {
      if (arrivalId == null || packageId == null) {
        throw new Error("Scan this container from Receive to load arrival context.");
      }
      return reprintInboundReceiveArrivalPackageLabel({
        arrivalId,
        packageId,
        printer: selectedPrinter,
      });
    },
    onSuccess: () => setPrintError(null),
    onError: (error) => setPrintError(error instanceof Error ? error.message : String(error)),
  });

  const hoverHint =
    props.route === "non_po_purchase"
      ? "Non-PO purchases are logged in Incoming Parts and expensed through Accounts Payable. This desk confirms the dock container is routed correctly before you record details there."
      : "When the devices on an open RMA have all been scanned, Receiving prints a sticker and sends the carton to the QC Analysis station.";

  const containerHeading =
    packageNo != null ? formatInboundArrivalUnitHeading(unitKind, packageNo) : null;

  return (
    <div className="space-y-4">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <PageTitleWithHelp hoverHint={hoverHint}>{pageTitle}</PageTitleWithHelp>
        <InboundZplPrinterHeaderButton
          station={station}
          onStationChange={setStation}
          unavailableMessage={unavailableMessage}
        
      documents={documents}
    />
      </header>
      <Card>
        <CardBody className="space-y-3">
          {props.route === "rma" && containerHeading != null ? (
            <>
              {countChecked && (rmaNumber !== "" || finish != null) ? (
                <p className="text-sm text-slate-800">
                  {rmaReceiveQuantityDiscrepancy
                    ? inboundReceiveRmaFinishWithDiscrepancyMessage(
                        rmaNumber !== "" ? rmaNumber : finish?.rmaNumber ?? "",
                      )
                    : inboundReceiveRmaCountCheckedMessage(
                        rmaNumber !== "" ? rmaNumber : finish?.rmaNumber ?? "",
                      )}
                </p>
              ) : (
                <p className="text-sm text-slate-800">
                  Scan each device in this carton. When you have finished checking them in, press Finished checking in.
                </p>
              )}
              {showDeviceScan ? (
                <div>
                  <Label htmlFor="inbound-rma-desk-device-scan">Device from this carton</Label>
                  <Input
                    id="inbound-rma-desk-device-scan"
                    ref={deviceScanRef}
                    value={deviceDraft}
                    className="mt-1 min-h-11 font-mono text-base"
                    placeholder="Scan a device label"
                    autoComplete="off"
                    onChange={(event) => setDeviceDraft(event.target.value)}
                    onKeyDown={(event) => {
                      if (event.key !== "Enter") return;
                      event.preventDefault();
                      addDeviceSerial(deviceDraft);
                    }}
                  />
                </div>
              ) : null}
              {checkInsQuery.isError ? (
                <p role="alert" className="text-sm text-rose-700">
                  {checkInsQuery.error instanceof Error
                    ? checkInsQuery.error.message
                    : "Could not load checked-in devices."}
                </p>
              ) : null}
              <table className="w-full border-collapse text-left text-sm">
                <thead>
                  <tr className="border-b border-slate-200">
                    <SortableTableHeader
                      label="Device"
                      sortKey="device"
                      activeKey={sortKey}
                      dir={sortDirection}
                      onSort={toggleCheckInSort}
                    />
                    <SortableTableHeader
                      label="SKU"
                      sortKey="sku"
                      activeKey={sortKey}
                      dir={sortDirection}
                      onSort={toggleCheckInSort}
                    />
                    <SortableTableHeader
                      label="End user"
                      sortKey="endUser"
                      activeKey={sortKey}
                      dir={sortDirection}
                      onSort={toggleCheckInSort}
                    />
                    {showDeviceScan ? <th className="w-12 bg-slate-50 px-2 py-2" /> : null}
                  </tr>
                </thead>
                <tbody>
                  {checkInsQuery.isLoading ? (
                    <tr>
                      <td className="py-2 pr-3 text-slate-500" colSpan={showDeviceScan ? 4 : 3}>
                        Loading checked-in devices…
                      </td>
                    </tr>
                  ) : checkInRows.length === 0 ? (
                    <tr>
                      <td className="py-2 pr-3 text-slate-500" colSpan={showDeviceScan ? 4 : 3}>
                        No devices checked in yet.
                      </td>
                    </tr>
                  ) : (
                    checkInRows.map((device) => (
                      <tr key={device.id} className="border-b border-slate-100">
                        <td className="px-2 py-2 font-mono text-slate-900">{device.deviceSerial}</td>
                        <td className="px-2 py-2 text-slate-800">{device.sku ?? ""}</td>
                        <td className="px-2 py-2 text-slate-800">{device.endUserName ?? ""}</td>
                        {showDeviceScan ? (
                          <td className="px-2 py-2 text-right">
                            {device.id === "pending" ? null : (
                              <button
                                type="button"
                                className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-slate-200 text-rose-700 hover:bg-rose-50"
                                aria-label={`Remove device ${device.deviceSerial}`}
                                title="Remove device"
                                disabled={deleteCheckInMutation.isPending}
                                onClick={() => deleteCheckInMutation.mutate(device.id)}
                              >
                                <Trash2 className="h-4 w-4" aria-hidden />
                              </button>
                            )}
                          </td>
                        ) : null}
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
              {showDeviceScan ? (
                <>
                  <div className="flex flex-wrap items-center gap-2">
                    <Button
                      type="button"
                      disabled={
                        scannedSerials.length === 0 ||
                        checkInMutation.isPending ||
                        printStickerMutation.isPending ||
                        (checkInFinished && deviceMatchQuery.isFetching)
                      }
                      onClick={() => {
                        autoPrintAttempt.current = false;
                        if (checkInFinished && readyToPrint) {
                          autoPrintAttempt.current = true;
                          printStickerMutation.mutate();
                          return;
                        }
                        setCheckInFinished(true);
                      }}
                    >
                      {printStickerMutation.isPending ? (
                        <Loader2 className="mr-1.5 h-4 w-4 animate-spin" aria-hidden />
                      ) : null}
                      Finished checking in
                    </Button>
                    {checkInFinished && deviceMatchQuery.isFetching ? (
                      <p className="flex items-center gap-2 text-sm text-slate-500">
                        <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                        Checking the devices on this RMA…
                      </p>
                    ) : null}
                    {readyToPrint && selectedPrinter.trim() === "" ? (
                      <p className="text-xs text-amber-800">Pick a label printer to print the sticker.</p>
                    ) : null}
                  </div>
                  {readyToPrint && finish != null ? (
                    quantityDiscrepancy ? (
                      <p className="text-sm font-medium text-amber-900">
                        {inboundReceiveRmaFinishWithDiscrepancyMessage(finish.rmaNumber)}
                      </p>
                    ) : (
                      <p className="text-sm font-medium text-emerald-800">
                        {inboundReceiveRmaCountCheckedMessage(finish.rmaNumber)}
                      </p>
                    )
                  ) : checkInFinished &&
                    deviceMatchQuery.isSuccess &&
                    !deviceMatchQuery.isFetching &&
                    scannedSerials.length === 0 ? (
                    <p className="text-sm text-slate-700">Scan at least one device before finishing check-in.</p>
                  ) : null}
                </>
              ) : null}
            </>
          ) : props.route === "non_po_purchase" && containerHeading != null ? (
            <>
              <p className="flex items-center gap-2 text-sm text-slate-700">
                <PackageSearch className="h-5 w-5 shrink-0 text-slate-500" aria-hidden />
                Arrival {containerHeading.toLowerCase()} is routed as {label}.
              </p>
              <p className="text-sm text-slate-600">
                Next: open{" "}
                <Link className="font-medium text-sky-700 underline" to="/mrp/incoming-parts">
                  Incoming Parts
                </Link>{" "}
                to log non-PO tracking, or receive ad-hoc lines if a miscellaneous purchase order is used.
              </p>
            </>
          ) : (
            <p className="flex items-center gap-2 text-sm text-slate-700">
              <PackageSearch className="h-5 w-5 shrink-0 text-slate-500" aria-hidden />
              Scan an ARRIVED tag from Receive to open this desk with container context.
            </p>
          )}
          {props.route === "rma" && countChecked && packageId != null && arrivalId != null ? (
            <div className="flex flex-wrap items-center gap-2 pt-1">
              <Button
                type="button"
                variant="secondary"
                disabled={reprintMutation.isPending || selectedPrinter.trim() === ""}
                onClick={() => reprintMutation.mutate()}
              >
                {reprintMutation.isPending ? (
                  <Loader2 className="mr-1.5 h-4 w-4 animate-spin" aria-hidden />
                ) : (
                  <Printer className="mr-1.5 h-4 w-4" aria-hidden />
                )}
                Reprint sticker
              </Button>
              {selectedPrinter.trim() === "" ? (
                <p className="text-xs text-amber-800">Pick a label printer to reprint the sticker.</p>
              ) : null}
            </div>
          ) : null}
          {printError ? (
            <p role="alert" className="text-sm text-rose-700">{printError}</p>
          ) : null}
        </CardBody>
      </Card>
    </div>
  );
}
